import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Upload, Download, Trash2 } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { TextField, TextAreaField, SelectField } from '../components/ui/Field';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { ResourceEditDrawer } from '../components/academics/ResourceEditDrawer';
import {
  humanBytes,
  type BatchRecord,
  type CourseSummary,
  type ResourceRecord,
} from '../components/academics/types';
import { formatDate } from '../lib/format';

const API_ORIGIN =
  (import.meta.env.VITE_API_ORIGIN as string | undefined) ??
  'https://api.babulilmonline.com';

const MANAGE_ROLES: ReadonlyArray<string> = [
  'super_admin',
  'academic_admin',
  'nalim',
  'teacher',
];

interface SignResponse {
  method: 'POST';
  url: string;
  token: string;
  key: string;
  expiresInSeconds: number;
}

export const Route = createFileRoute('/_authed/academics/resources')({
  component: ResourcesPage,
});

function ResourcesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage = role !== null && MANAGE_ROLES.includes(role);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ResourceRecord | null>(null);

  const listQuery = useQuery({
    queryKey: ['resources', 'list'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: ResourceRecord[] }>('/api/resources', { signal }),
  });

  const batchesQuery = useQuery({
    queryKey: ['batches', 'resources-lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: BatchRecord[] }>('/api/batches', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const coursesQuery = useQuery({
    queryKey: ['courses', 'resources-lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: CourseSummary[] }>('/api/courses', { signal }),
    staleTime: 60_000,
  });

  const batchLookup = useMemo(() => {
    const map = new Map<string, BatchRecord>();
    for (const b of batchesQuery.data?.items ?? []) map.set(b.id, b);
    return map;
  }, [batchesQuery.data]);
  const courseLookup = useMemo(() => {
    const map = new Map<string, CourseSummary>();
    for (const c of coursesQuery.data?.items ?? []) map.set(c.id, c);
    return map;
  }, [coursesQuery.data]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok: true }>(`/api/resources/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['resources'] });
      toast('Resource deleted.', { variant: 'success' });
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not delete.', {
        variant: 'error',
      });
    },
  });

  const handleDownload = async (id: string) => {
    try {
      const res = await apiRequest<{ url: string; expiresInSeconds: number }>(
        `/api/resources/${id}/download-url`,
      );
      window.open(`${API_ORIGIN}${res.url}`, '_blank', 'noopener');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not start download.', {
        variant: 'error',
      });
    }
  };

  const handleDelete = async (id: string, title: string) => {
    const ok = await confirm({
      title: 'Delete resource?',
      description: `"${title}" will be removed for everyone with access.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    deleteMutation.mutate(id);
  };

  const columns = useMemo<ColumnDef<ResourceRecord, unknown>[]>(() => {
    const base: ColumnDef<ResourceRecord, unknown>[] = [
      {
        id: 'title',
        header: 'Title',
        cell: (info) => (
          <span className="font-medium text-[color:var(--color-text)]">
            {info.row.original.title}
          </span>
        ),
      },
      {
        id: 'scope',
        header: 'Scope',
        cell: (info) => {
          const r = info.row.original;
          if (r.batchId) {
            const b = batchLookup.get(r.batchId);
            return (
              <span className="text-[color:var(--color-text-muted)]">
                Batch · {b ? b.name : r.batchId.slice(0, 8)}
              </span>
            );
          }
          if (r.courseId) {
            const c = courseLookup.get(r.courseId);
            return (
              <span className="text-[color:var(--color-text-muted)]">
                Course · {c ? c.name : r.courseId.slice(0, 8)}
              </span>
            );
          }
          return <span className="text-[color:var(--color-text-muted)]">—</span>;
        },
      },
      {
        id: 'mimeType',
        header: 'Type',
        cell: (info) => (
          <span className="font-mono text-xs text-[color:var(--color-text-muted)]">
            {info.row.original.mimeType}
          </span>
        ),
      },
      {
        id: 'size',
        header: 'Size',
        cell: (info) => (
          <span className="tabular-nums text-[color:var(--color-text-muted)]">
            {humanBytes(info.row.original.sizeBytes)}
          </span>
        ),
      },
      {
        id: 'uploaded',
        header: 'Uploaded',
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)]">
            {formatDate(info.row.original.createdAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className="flex items-center justify-end gap-1">
              {canManage ? (
                <EditIconButton
                  label={`Edit ${r.title}`}
                  onClick={() => setEditTarget(r)}
                />
              ) : null}
              <Button
                size="sm"
                variant="secondary"
                icon={<Download size={14} aria-hidden="true" />}
                onClick={(e) => {
                  // Row click navigates to the detail page; stop propagation
                  // so clicking Download doesn't both download AND navigate.
                  e.stopPropagation();
                  void handleDownload(r.id);
                }}
              >
                Download
              </Button>
              <Button
                size="sm"
                variant="ghost"
                icon={<Trash2 size={14} aria-hidden="true" />}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDelete(r.id, r.title);
                }}
                aria-label={`Delete ${r.title}`}
                className="text-danger hover:bg-[color:var(--color-danger)]/10"
              >
                Delete
              </Button>
            </div>
          );
        },
      },
    ];
    return base;
  }, [batchLookup, courseLookup, canManage]);

  return (
    <ListOrOutlet basePath="/academics/resources">
    <ListShell
      title="Resources"
      description="Files shared with batches or courses."
      primaryAction={
        <Button
          onClick={() => setUploadOpen(true)}
          icon={<Upload size={16} aria-hidden="true" />}
        >
          Upload resource
        </Button>
      }
    >
      {listQuery.isError ? (
        <ErrorState
          title="Could not load resources"
          description={
            listQuery.error instanceof ApiError ? listQuery.error.message : undefined
          }
          onRetry={() => listQuery.refetch()}
        />
      ) : (
        <DataTable<ResourceRecord>
          data={listQuery.data?.items ?? []}
          columns={columns}
          isLoading={listQuery.isLoading && !listQuery.data}
          getRowId={(row) => row.id}
          onRowClick={(row) =>
            void navigate({
              to: '/academics/resources/$resourceId',
              params: { resourceId: row.id },
            })
          }
          emptyState={
            <EmptyState
              title="No resources yet"
              description="Upload course or batch materials so everyone has the same source."
              action={
                <Button
                  onClick={() => setUploadOpen(true)}
                  icon={<Upload size={16} aria-hidden="true" />}
                >
                  Upload resource
                </Button>
              }
            />
          }
        />
      )}

      <UploadDrawer
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        batches={batchesQuery.data?.items ?? []}
        courses={coursesQuery.data?.items ?? []}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['resources'] });
          setUploadOpen(false);
        }}
      />

      {canManage ? (
        <ResourceEditDrawer
          open={editTarget !== null}
          onClose={() => setEditTarget(null)}
          resource={editTarget}
          batches={batchesQuery.data?.items ?? []}
          courses={coursesQuery.data?.items ?? []}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['resources'] });
            setEditTarget(null);
          }}
        />
      ) : null}
    </ListShell>
    </ListOrOutlet>
  );
}

interface UploadDrawerProps {
  open: boolean;
  onClose: () => void;
  batches: ReadonlyArray<BatchRecord>;
  courses: ReadonlyArray<CourseSummary>;
  onDone: () => void;
}

type Scope = 'batch' | 'course';

function UploadDrawer({ open, onClose, batches, courses, onDone }: UploadDrawerProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<Scope>('batch');
  const [batchId, setBatchId] = useState<string>('');
  const [courseId, setCourseId] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'signing' | 'uploading' | 'finalizing'>(
    'idle',
  );

  const reset = () => {
    setFile(null);
    setTitle('');
    setDescription('');
    setScope('batch');
    setBatchId('');
    setCourseId('');
    setStatus('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast('Choose a file to upload.', { variant: 'error' });
      return;
    }
    if (!title.trim()) {
      toast('Enter a title.', { variant: 'error' });
      return;
    }
    const linkedBatch = scope === 'batch' ? batchId : '';
    const linkedCourse = scope === 'course' ? courseId : '';
    if (!linkedBatch && !linkedCourse) {
      toast('Pick a batch or course to link the resource to.', { variant: 'error' });
      return;
    }

    try {
      setStatus('signing');
      const sign = await apiRequest<SignResponse>('/api/uploads/sign', {
        method: 'POST',
        body: {
          purpose: 'resource',
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        },
      });

      setStatus('uploading');
      const uploadRes = await fetch(sign.url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'x-upload-token': sign.token,
          'content-type': file.type || 'application/octet-stream',
        },
        body: file,
      });
      if (!uploadRes.ok) {
        const errJson = (await uploadRes.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new ApiError(
          uploadRes.status,
          'upload_failed',
          errJson.message ?? 'Upload failed.',
        );
      }

      setStatus('finalizing');
      await apiRequest<{ id: string }>('/api/resources', {
        method: 'POST',
        body: {
          r2Key: sign.key,
          title: title.trim(),
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(linkedBatch ? { batchId: linkedBatch } : {}),
          ...(linkedCourse ? { courseId: linkedCourse } : {}),
        },
      });

      toast('Resource uploaded.', { variant: 'success' });
      onDone();
      reset();
    } catch (err) {
      setStatus('idle');
      toast(err instanceof ApiError ? err.message : 'Could not upload.', {
        variant: 'error',
      });
    }
  };

  const batchOptions = [
    { value: '', label: batches.length ? 'Select a batch' : 'No batches' },
    ...batches.map((b) => ({ value: b.id, label: b.name })),
  ];
  const courseOptions = [
    { value: '', label: courses.length ? 'Select a course' : 'No courses' },
    ...courses.map((c) => ({ value: c.id, label: c.name })),
  ];

  const isUploading = status !== 'idle';

  return (
    <Drawer open={open} onClose={close} title="Upload resource" size="md">
      <form onSubmit={submit} noValidate className="space-y-5">
        <div>
          <p className="text-sm font-medium">File</p>
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => {
              const next = e.target.files?.[0] ?? null;
              setFile(next);
              if (next && !title.trim()) {
                // Pre-fill title from file name minus extension.
                const base = next.name.replace(/\.[^.]+$/u, '');
                setTitle(base);
              }
            }}
            className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border file:border-[color:var(--color-border)] file:bg-[color:var(--color-bg-elevated)] file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-[color:var(--color-text)]"
            disabled={isUploading}
          />
          {file ? (
            <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
              {file.name} · {humanBytes(file.size)}
            </p>
          ) : null}
        </div>

        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isUploading}
        />
        <TextAreaField
          label="Description"
          optional
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isUploading}
        />

        <div>
          <p className="text-sm font-medium">Link to</p>
          <div role="radiogroup" aria-label="Scope" className="mt-2 inline-flex rounded-md border border-[color:var(--color-border)] p-0.5">
            {(['batch', 'course'] as const).map((kind) => {
              const isActive = scope === kind;
              return (
                <button
                  key={kind}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => setScope(kind)}
                  disabled={isUploading}
                  className={
                    isActive
                      ? 'rounded px-3 py-1 text-xs font-medium bg-gold-50 text-gold-700'
                      : 'rounded px-3 py-1 text-xs font-medium text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]'
                  }
                >
                  {kind === 'batch' ? 'Batch' : 'Course'}
                </button>
              );
            })}
          </div>
        </div>

        {scope === 'batch' ? (
          <SelectField
            label="Batch"
            options={batchOptions}
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            disabled={isUploading}
          />
        ) : (
          <SelectField
            label="Course"
            options={courseOptions}
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
            disabled={isUploading}
          />
        )}

        <div className="flex items-center justify-between pt-2">
          {isUploading ? (
            <p className="text-xs text-[color:var(--color-text-muted)]">
              {status === 'signing'
                ? 'Preparing upload…'
                : status === 'uploading'
                  ? 'Uploading file…'
                  : 'Saving record…'}
            </p>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={close} disabled={isUploading}>
              Cancel
            </Button>
            <Button type="submit" loading={isUploading} disabled={!file}>
              Upload
            </Button>
          </div>
        </div>
      </form>
    </Drawer>
  );
}
